/**
 * Customer API surface (F2 backend, F2 frontend added on top of the mobile lead-capture
 * flow that has been the sole consumer of `createCustomer` until now).
 *
 * Follows the same `apiGet`/`Page<T>` conventions as `features/orders/api.ts` rather than
 * the raw `apiFetch` this file used to export -- see the module notes on `CustomerRead`
 * for why the two write shapes (`CustomerCreate`, `CustomerUpdate`) differ by exactly the
 * fields `code`/`tier`/`status`/`assigned_rep_id` default on create and cannot be blanked
 * on update.
 */

import { API_PREFIX, apiBlob, apiFetch, apiGet, type Page } from '@/api/client'
import type { ExportFormat } from '@/components/ExportButton'

export const CUSTOMER_TIERS = ['standard', 'silver', 'gold', 'platinum'] as const
export type CustomerTier = (typeof CUSTOMER_TIERS)[number]

export const CUSTOMER_STATUSES = ['active', 'inactive', 'blocked'] as const
export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number]

export interface CustomerCreate {
  code: string
  name: string
  email?: string
  phone?: string
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  latitude?: number
  longitude?: number
  tax_id?: string
  tax_exempt?: boolean
  credit_limit?: number | string
  payment_terms_days?: number
  notes?: string
  tier?: string
  status?: string
  assigned_rep_id?: string | null
}

/** Every `CustomerCreate` field but `code`: the router's `CustomerUpdate` has no code, and
 *  `tier`/`status`/`assigned_rep_id` stay optional rather than gaining a default. */
export type CustomerUpdate = Omit<CustomerCreate, 'code'>

export interface CustomerRead {
  id: string
  code: string
  name: string
  email: string | null
  phone: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  country: string
  latitude: string | null
  longitude: string | null
  tax_id: string | null
  tax_exempt: boolean
  credit_limit: string | null
  payment_terms_days: number
  notes: string | null
  tier: string
  status: string
  assigned_rep_id: string | null
  created_at: string
  updated_at: string
}

export interface CustomerFilters {
  page?: number
  q?: string
  tier?: string
  status?: string
  assigned_rep_id?: string
  unassigned?: boolean
}

function query(filters: CustomerFilters): string {
  const params = new URLSearchParams()
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  if (filters.q) params.set('q', filters.q)
  if (filters.tier) params.set('tier', filters.tier)
  if (filters.status) params.set('status', filters.status)
  if (filters.assigned_rep_id) params.set('assigned_rep_id', filters.assigned_rep_id)
  if (filters.unassigned) params.set('unassigned', 'true')
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const listCustomers = (filters: CustomerFilters = {}) =>
  apiFetch<Page<CustomerRead>>(`${API_PREFIX}/customers${query(filters)}`)

export const getCustomer = (id: string) => apiGet<CustomerRead>(`${API_PREFIX}/customers/${id}`)

export const exportCustomers = (filters: CustomerFilters, format: ExportFormat) => {
  const qs = query(filters)
  return apiBlob(`${API_PREFIX}/customers/export${qs ? `${qs}&fmt=${format}` : `?fmt=${format}`}`)
}

export const createCustomer = (data: CustomerCreate) =>
  apiGet<CustomerRead>(`${API_PREFIX}/customers`, {
    method: 'POST',
    body: JSON.stringify(data),
  })

export const updateCustomer = (id: string, data: CustomerUpdate) =>
  apiGet<CustomerRead>(`${API_PREFIX}/customers/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })

/**
 * Hand the account to another rep, or unassign it. Gated on `customers:delete` in the
 * matrix, not `:update` -- the router's own words are "handing an account to another rep
 * removes it from the current one's book, and the matrix has no cell finer than this to
 * express that", which is why the Customers screen asks for delete rights before showing
 * the reassign control rather than the update ones every other field uses.
 */
export const reassignCustomer = (id: string, assignedRepId: string | null) =>
  apiGet<CustomerRead>(`${API_PREFIX}/customers/${id}/assignment`, {
    method: 'PUT',
    body: JSON.stringify({ assigned_rep_id: assignedRepId }),
  })

/** Soft delete -- see the router's own summary. */
export const deleteCustomer = (id: string) =>
  apiFetch<void>(`${API_PREFIX}/customers/${id}`, { method: 'DELETE' })

// --- nearby leads --------------------------------------------------------------------------
// Shops Google Places knows about that are not customers yet. See `customers.leads` on the
// server for how the score is made; every part of it comes back in `reasons`.

export interface Lead {
  place_id: string
  name: string
  address: string | null
  latitude: number
  longitude: number
  phone: string | null
  primary_type: string | null
  rating: number | null
  rating_count: number
  distance_m: number
  score: number
  priority: 'hot' | 'warm' | 'cold'
  reasons: string[]
}

export interface NearbyLeads {
  leads: Lead[]
  unavailable_reason: string | null
  already_customers: number
}

export const findNearbyLeads = (latitude: number, longitude: number, radius_m: number) =>
  apiGet<NearbyLeads>(
    `${API_PREFIX}/customers/leads/nearby?${new URLSearchParams({
      latitude: String(latitude),
      longitude: String(longitude),
      radius_m: String(radius_m),
    })}`,
  )
